import React from "react";

/**
 * WS-7 - Research transparency panel.
 *
 * Renders the canonical metadata block that every real research
 * surface (analysis response, homepage card, portfolio review,
 * locked preview, PDF export) already carries.  Consumers pass
 * ``metadata`` (the ``ws7_metadata`` block) plus an optional
 * ``compact`` flag for card-footer variants.
 */

const fmtDate = (v) => {
  if (!v) return "Unavailable";
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return "Unavailable";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "Unavailable";
  }
};

export const TransparencyPanel = ({ metadata, compact = false }) => {
  if (!metadata || typeof metadata !== "object") {
    return (
      <div data-testid="ws7-transparency-empty" className="text-xs text-muted-foreground italic">
        Research metadata unavailable.
      </div>
    );
  }
  const {
    data_as_of,
    valuation_as_of,
    analysis_generated_at,
    last_updated_at,
    model_version,
    methodology_version,
    schema_version,
    regulatory_copy_version,
    data_quality_status,
    stale_data,
    content_nature,
    provider_category,
    source_categories = [],
    correction_notice,
  } = metadata;

  if (compact) {
    return (
      <div data-testid="ws7-transparency-compact" className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
        <span data-testid="ws7-data-as-of">
          <strong>Data as of:</strong> {fmtDate(data_as_of)}
        </span>
        <span data-testid="ws7-analysis-updated">
          <strong>Analysis updated:</strong> {fmtDate(last_updated_at || analysis_generated_at)}
        </span>
        <span data-testid="ws7-model-version">
          <strong>Model:</strong> {model_version || "unavailable"}
        </span>
        {stale_data && stale_data !== "none" && (
          <span data-testid="ws7-stale-badge" className="text-amber-600">
            Stale: {stale_data}
          </span>
        )}
        {correction_notice && (
          <span data-testid="ws7-correction-notice" className="text-amber-700">
            {correction_notice.label}
          </span>
        )}
      </div>
    );
  }

  return (
    <div data-testid="ws7-transparency-panel" className="border rounded-md p-3 text-xs bg-muted/30 space-y-1">
      <div className="font-semibold text-sm mb-1" data-testid="ws7-panel-title">
        Research transparency
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <div><strong>Data as of:</strong> <span data-testid="ws7-panel-data-as-of">{fmtDate(data_as_of)}</span></div>
        <div><strong>Valuation calculated:</strong> <span data-testid="ws7-panel-valuation-as-of">{fmtDate(valuation_as_of)}</span></div>
        <div><strong>Analysis generated:</strong> <span data-testid="ws7-panel-analysis-generated">{fmtDate(analysis_generated_at)}</span></div>
        <div><strong>Last updated:</strong> <span data-testid="ws7-panel-last-updated">{fmtDate(last_updated_at)}</span></div>
        <div><strong>Model version:</strong> <span data-testid="ws7-panel-model">{model_version || "unavailable"}</span></div>
        <div><strong>Methodology:</strong> <span data-testid="ws7-panel-methodology">{methodology_version || "unavailable"}</span></div>
        <div><strong>Schema:</strong> <span data-testid="ws7-panel-schema">{schema_version || "unavailable"}</span></div>
        <div><strong>Regulatory copy:</strong> <span data-testid="ws7-panel-regcopy">{regulatory_copy_version || "unavailable"}</span></div>
        <div><strong>Content:</strong> <span data-testid="ws7-panel-content-nature">{content_nature || "unavailable"}</span></div>
        <div><strong>Provider:</strong> <span data-testid="ws7-panel-provider">{provider_category || "unavailable"}</span></div>
      </div>
      <div><strong>Sources:</strong> <span data-testid="ws7-panel-sources">{source_categories.length ? source_categories.join(", ") : "unavailable"}</span></div>
      <div><strong>Data quality:</strong> <span data-testid="ws7-panel-dq">{data_quality_status || "unavailable"}</span>
        {stale_data && stale_data !== "none" && (
          <span data-testid="ws7-panel-stale" className="text-amber-600"> · Stale: {stale_data}</span>
        )}
      </div>
      {correction_notice && (
        <div data-testid="ws7-panel-correction" className="text-amber-700 font-medium">
          {correction_notice.label}
          {correction_notice.as_of ? ` (as of ${fmtDate(correction_notice.as_of)})` : ""}
        </div>
      )}
      <div className="text-muted-foreground italic pt-1" data-testid="ws7-panel-disclaimer">
        Stockaily research is model-based. Not personalised financial advice.
      </div>
    </div>
  );
};

export default TransparencyPanel;
